import { useEffect, useState } from 'react';
import { api, formatINR, formatApiError } from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { RefreshCw, Loader2, Wrench, Search, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

/** One scan+fix block: correction-ledger duplicates, or stale credit collections. */
const Section = ({ title, hint, endpoint, testId, scannedLabel, countOf, renderItem, emptyLabel }) => {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState('');

  const run = async (fix) => {
    setBusy(fix ? 'fix' : 'scan');
    try {
      const { data } = await api.post(endpoint, null, { params: { fix } });
      if (fix) {
        toast.success(countOf(data) === 0 ? 'Nothing left to fix' : `Fixed ${countOf(data)} record(s)`);
        const rescan = await api.post(endpoint, null, { params: { fix: false } });
        setReport(rescan.data);
      } else {
        setReport(data);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not run this check');
    } finally {
      setBusy('');
    }
  };

  const count = report ? countOf(report) : null;

  return (
    <div className="rounded-lg border border-border/60 p-3 space-y-2.5" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
        </div>
        {count !== null && (
          <Badge variant={count === 0 ? 'outline' : 'destructive'} className="shrink-0" data-testid={`${testId}-count`}>
            {count === 0 ? 'all clear' : `${count} found`}
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => run(false)} disabled={!!busy} data-testid={`${testId}-scan`}>
          {busy === 'scan' ? <Loader2 size={14} className="animate-spin mr-1.5"/> : <RefreshCw size={14} className="mr-1.5"/>}
          Scan (dry run)
        </Button>
        <Button size="sm" onClick={() => run(true)} disabled={!!busy || !report || count === 0} data-testid={`${testId}-fix`}>
          {busy === 'fix' ? <Loader2 size={14} className="animate-spin mr-1.5"/> : null}
          Apply fix{count ? ` (${count})` : ''}
        </Button>
      </div>
      {report && (
        <div className="text-[11px] text-muted-foreground">
          Scanned {scannedLabel(report)} record(s){report.fixed ? ' · fix applied' : ''}.
        </div>
      )}
      {report && (report.items || []).length > 0 && (
        <div className="max-h-52 overflow-auto thin-scroll rounded border border-border/40 divide-y divide-border/40">
          {report.items.map((it, i) => (
            <div key={i} className="px-2.5 py-1.5 text-xs" data-testid={`${testId}-row-${i}`}>
              {renderItem(it)}
            </div>
          ))}
        </div>
      )}
      {report && (report.items || []).length === 0 && (
        <div className="text-[11px] text-muted-foreground">{emptyLabel}</div>
      )}
    </div>
  );
};

/** Raw look-up for one bill: what every collection this app knows about actually
    holds for it — its own sale, its credit's collection history, and every cash,
    bank and UPI/card ledger row that references it. Read-only. */
const LookupSection = () => {
  const [ref, setRef] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!ref.trim()) return;
    setBusy(true);
    try {
      const { data: d } = await api.get('/store/admin/data-health/lookup', { params: { ref: ref.trim() } });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not look that up');
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const Row = ({ label, children }) => (
    <div className="flex items-start justify-between gap-3 text-xs py-1 border-b border-border/30 last:border-0">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );

  return (
    <div className="rounded-lg border border-border/60 p-3 space-y-2.5" data-testid="lookup-bill-section">
      <div>
        <div className="text-sm font-semibold">Look up a bill</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          See every row this app holds for one bill or sale number — read-only, changes nothing.
        </div>
      </div>
      <div className="flex gap-2">
        <Input
          value={ref} onChange={(e) => setRef(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
          placeholder="e.g. SL-2026-00102 or a bill number"
          className="h-9 text-sm" data-testid="lookup-bill-input"
        />
        <Button size="sm" onClick={run} disabled={busy || !ref.trim()} data-testid="lookup-bill-btn">
          {busy ? <Loader2 size={14} className="animate-spin mr-1.5"/> : <Search size={14} className="mr-1.5"/>}
          Look up
        </Button>
      </div>
      {data && !data.sale && (
        <div className="text-[11px] text-muted-foreground" data-testid="lookup-bill-not-found">
          No bill found with that number.
        </div>
      )}
      {data && data.sale && (
        <div className="space-y-2.5" data-testid="lookup-bill-result">
          <div className="rounded border border-border/40 px-2.5 py-1.5">
            <Row label="Sale">{data.sale.sale_number} · ₹{data.sale.amount} · {data.sale.business_date}</Row>
            <Row label="Splits">{Object.entries(data.sale.splits || {}).filter(([, v]) => v)
              .map(([k, v]) => `${k}: ${formatINR(v)}`).join(' · ') || 'none'}</Row>
            {data.sale.voided && <Row label="Voided">yes · {data.sale.voided_at}</Row>}
          </div>
          {data.credit && (
            <div className="rounded border border-border/40 px-2.5 py-1.5">
              <Row label="Credit">₹{data.credit.amount} · paid ₹{data.credit.paid} · outstanding ₹{data.credit.outstanding} · {data.credit.status}</Row>
              {(data.credit.collections || []).map((c, i) => (
                <Row key={i} label={`Collection ${i + 1}`}>{c.mode} · ₹{c.amount} · {c.date} · by {c.by}</Row>
              ))}
            </div>
          )}
          <div className="rounded border border-border/40 px-2.5 py-1.5">
            <Row label="Cash ledger rows">{data.cash_entries.length}</Row>
            {data.cash_entries.map((r, i) => (
              <Row key={i} label={r.purpose || '—'}>₹{r.amount} · {r.txn_date} · {r.kind}</Row>
            ))}
          </div>
          <div className="rounded border border-border/40 px-2.5 py-1.5">
            <Row label="Bank ledger rows">{data.transactions.length}</Row>
            {data.transactions.map((r, i) => (
              <Row key={i} label={r.source || '—'}>₹{r.amount} · {r.txn_date} · {r.kind}</Row>
            ))}
          </div>
          <div className="rounded border border-border/40 px-2.5 py-1.5">
            <Row label="UPI/card ledger rows">{data.settlement_items.length}</Row>
            {data.settlement_items.map((r, i) => (
              <Row key={i} label={r.source || '—'}>
                {r.mode} · ₹{r.amount} · {r.business_date} · {r.status}
              </Row>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** Sets/rotates the single global Super Admin PIN — works at every counter (no
    per-outlet or per-staff scoping) to authorise sensitive counter actions such as
    correcting a settled credit's payment history. */
const SuperAdminPinSection = () => {
  const [status, setStatus] = useState(null);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/store/admin/super-admin-pin').then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!/^\d{6}$/.test(pin)) { toast.error('PIN must be exactly 6 digits'); return; }
    if (pin !== confirmPin) { toast.error("The two PINs don't match"); return; }
    setBusy(true);
    try {
      await api.post('/store/admin/super-admin-pin', { pin });
      toast.success('Super Admin PIN saved');
      setPin(''); setConfirmPin('');
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Could not save the PIN');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border/60 p-3 space-y-2.5" data-testid="super-admin-pin-section">
      <div>
        <div className="text-sm font-semibold flex items-center gap-1.5"><KeyRound size={15}/>Super Admin PIN</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          One universal 6-digit PIN that works at every counter — lets a counter correct or remove a
          settled credit's past collection with your approval, without needing you to open its session.
        </div>
      </div>
      {status && (
        <div className="text-[11px] text-muted-foreground" data-testid="super-admin-pin-status">
          {status.pin_set
            ? `Set${status.updated_by ? ` by ${status.updated_by}` : ''}${status.updated_at ? ` · ${status.updated_at.slice(0, 10)}` : ''}`
            : 'Not set yet — no counter can use this until you set one.'}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Input type="password" inputMode="numeric" maxLength={6} placeholder="New 6-digit PIN" value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="h-9 text-sm w-40" data-testid="super-admin-pin-input"/>
        <Input type="password" inputMode="numeric" maxLength={6} placeholder="Confirm PIN" value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="h-9 text-sm w-40" data-testid="super-admin-pin-confirm-input"/>
        <Button size="sm" onClick={save} disabled={busy || pin.length !== 6 || confirmPin.length !== 6}
          data-testid="super-admin-pin-save-btn">
          {busy ? <Loader2 size={14} className="animate-spin mr-1.5"/> : null}
          {status?.pin_set ? 'Rotate PIN' : 'Set PIN'}
        </Button>
      </div>
    </div>
  );
};

export const AdminLedgerCleanup = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        data-testid="admin-ledger-cleanup-btn"
        variant="ghost" size="icon"
        title="Ledger cleanup (Super Admin)"
        onClick={() => setOpen(true)}
      >
        <Wrench size={17}/>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-ledger-cleanup-dialog">
          <DialogHeader>
            <DialogTitle>Ledger cleanup</DialogTitle>
            <DialogDescription>
              One-time fixes for sales corrected/cancelled before the ledger-consolidation fix, and
              credits left stale by an older collection-delete bug. Scan first — nothing changes until
              you click Apply fix. Safe to run again any time; a clean re-scan reports zero.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <SuperAdminPinSection/>
            <Section
              title="Duplicate correction/void rows"
              hint="Bills corrected or cancelled before the fix may still show a second cash/bank ledger row. This merges them into the bill's own row — no balance moves."
              endpoint="/store/admin/data-health/correction-ledger"
              testId="cleanup-correction-ledger"
              scannedLabel={(r) => r.sales_scanned}
              countOf={(r) => (r.items || []).length}
              emptyLabel="No corrected or cancelled bill has a duplicate row."
              renderItem={(it) => (
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{it.sale_number}</span>
                  <span className="text-muted-foreground">
                    {it.mode} · {it.rows} rows → {formatINR(Math.abs(it.net))}
                  </span>
                </div>
              )}
            />
            <Section
              title="Stale credit collections"
              hint="Credits whose collection was deleted or reversed before the fix may still show as fully paid. This recalculates paid/outstanding from real evidence."
              endpoint="/store/admin/data-health/credit-collections"
              testId="cleanup-credit-collections"
              scannedLabel={(r) => r.scanned}
              countOf={(r) => r.mismatches ?? (r.items || []).length}
              emptyLabel="Every credit's paid/outstanding matches its real collections."
              renderItem={(it) => (
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">{it.customer_name || it.sale_number}</span>
                  <span className="text-muted-foreground">
                    {formatINR(it.recorded_paid)} → {formatINR(it.actual_paid)} paid
                  </span>
                </div>
              )}
            />
          </div>
          <div className="pt-1">
            <LookupSection />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
